import React, { useMemo, useState } from "react";
import {
  FaHeart,
  FaPlus,
  FaRegCalendarAlt,
  FaRegHeart,
  FaShareAlt,
} from "react-icons/fa";
import {
  fmtBadge,
  fmtTimeRange,
  shareEvent,
  toIsoLocal, eventEmoji } from "./eventsApi";
import "./CommunityEvents.css";

/* =========================================================
   CommunityEvents   the Events tab inside CommunityShowcase.
   Presentation-only: feed it state from useCommunityEvents.

     <CommunityEvents
       community="Mexican"
       events={events} importantDates={importantDates}
       loading={loading} error={error}
       onOpenEvent={(ev) => …}      // card / Details tap
       onPostEvent={fn | null}      // gold Post button
       isEventSaved={fn} onToggleSave={fn}
     />

   Renders inside <section class="ci"> so it inherits the
   --ci-* palette vars and can reuse ci-ph-* / ci-act / ci-note
   classes from CommunityShowcase.css.
   ========================================================= */

const STRIP_DAYS = 14;

export default function CommunityEvents({
  community = "Community",
  eventsLabel = "Events",
  events = [],
  importantDates = [],
  loading = false,
  error = "",
  onOpenEvent = null,
  onPostEvent = null,
  isEventSaved = () => false,
  onToggleSave = null,
}) {
  const [dayIso, setDayIso] = useState(""); /* "" = all upcoming */

  const upcoming = useMemo(() => {
    const todayIso = toIsoLocal(new Date());
    return events
      .filter((ev) => String(ev?.event_date || "") >= todayIso)
      .sort((a, b) =>
        a.event_date === b.event_date
          ? String(a.start_time || "").localeCompare(String(b.start_time || ""))
          : String(a.event_date).localeCompare(String(b.event_date))
      );
  }, [events]);

  const eventDays = useMemo(
    () => new Set(upcoming.map((ev) => ev.event_date)),
    [upcoming]
  );

  const strip = useMemo(() => {
    const out = [];
    const base = new Date();
    for (let i = 0; i < STRIP_DAYS; i += 1) {
      const d = new Date(
        base.getFullYear(),
        base.getMonth(),
        base.getDate() + i
      );
      out.push({
        iso: toIsoLocal(d),
        dow: d.toLocaleDateString(undefined, { weekday: "short" }),
        num: d.getDate(),
      });
    }
    return out;
  }, []);

  const shown = dayIso
    ? upcoming.filter((ev) => ev.event_date === dayIso)
    : upcoming;

  const headingText = `Upcoming ${eventsLabel.toLowerCase()}`;
  const emptyText =
    eventsLabel === "Events"
      ? `No ${community} events yet   be the first to share what's happening in the community.`
      : `No ${eventsLabel.toLowerCase()} yet   be the first to share what's happening around town.`;

  const postTap = () => {
    if (typeof onPostEvent === "function") onPostEvent();
    else window.alert("Event posting is coming soon.");
  };

  return (
    <div className="cev">
      <div className="cev-head">
        <h2>{headingText}</h2>
        {upcoming.length > 0 && (
          <span className="cev-count">{upcoming.length}</span>
        )}
        <button type="button" className="cev-post" onClick={postTap}>
          <FaPlus aria-hidden="true" /> Post event
        </button>
      </div>

      {loading && (
        <p className="ci-note" role="status">Loading events…</p>
      )}
      {!loading && error && (
        <p className="ci-note ci-note--err" role="alert">{error}</p>
      )}

      {!loading && !error && upcoming.length === 0 && (
        <div className="cev-empty">
          <span className="cev-empty__ic" aria-hidden="true">📅</span>
          <p>{emptyText}</p>
        </div>
      )}

      {!loading && !error && upcoming.length > 0 && (
        <>
          <div
            className="cev-strip"
            role="tablist"
            aria-label="Filter events by day"
          >
            <button
              type="button"
              role="tab"
              aria-selected={dayIso === ""}
              className={`cev-day cev-day--all ${dayIso === "" ? "is-on" : ""}`}
              onClick={() => setDayIso("")}
            >
              <b>All</b>
            </button>

            {strip.map((day) => (
              <button
                key={day.iso}
                type="button"
                role="tab"
                aria-selected={dayIso === day.iso}
                className={`cev-day ${eventDays.has(day.iso) ? "has-ev" : ""} ${
                  dayIso === day.iso ? "is-on" : ""
                }`}
                onClick={() =>
                  setDayIso((prev) => (prev === day.iso ? "" : day.iso))
                }
              >
                <small>{day.dow}</small>
                <b>{day.num}</b>
                <i aria-hidden="true" />
              </button>
            ))}
          </div>

          {shown.length === 0 && (
            <p className="ci-note">
              Nothing on this day yet   tap another date or All.
            </p>
          )}

          <div className="cev-list">
            {shown.map((ev, index) => {
              const saved = isEventSaved(ev.id);

              return (
                <div key={ev.id} className="cev-card">
                  <button
                    type="button"
                    className="cev-card__main"
                    onClick={() => {
                      if (typeof onOpenEvent === "function") onOpenEvent(ev);
                    }}
                  >
                    <span
                      className={`cev-fly ci-ph-${index % 4}`}
                      aria-hidden="true"
                    >
                      {ev.image_urls?.[0] ? (
                        <img src={ev.image_urls[0]} alt="" loading="lazy" />
                      ) : (
                        ev.icon || eventEmoji(ev)
                      )}
                      <span className="cev-badge">{fmtBadge(ev.event_date)}</span>
                    </span>

                    <span className="cev-card__bd">
                      <span className="cev-card__nm">{ev.title}</span>
                      <span className="cev-card__tg">
                        {[fmtTimeRange(ev), ev.venue_name]
                          .filter(Boolean)
                          .join(" · ")}
                      </span>
                      {Array.isArray(ev.tags) && ev.tags.length > 0 && (
                        <span className="cev-card__tags">
                          {ev.tags.join(" · ")}
                        </span>
                      )}
                    </span>
                  </button>

                  <span className="cev-card__acts">
                    <button
                      type="button"
                      className={`ci-act ${saved ? "is-on" : ""}`}
                      onClick={() => {
                        if (typeof onToggleSave === "function") onToggleSave(ev);
                      }}
                      aria-pressed={saved}
                      aria-label={saved ? "Remove from saved" : "Save event"}
                    >
                      {saved ? <FaHeart /> : <FaRegHeart />}
                    </button>

                    <button
                      type="button"
                      className="ci-act"
                      onClick={() => shareEvent(ev)}
                      aria-label="Share event"
                    >
                      <FaShareAlt />
                    </button>
                  </span>
                </div>
              );
            })}
          </div>
        </>
      )}

      {!loading && importantDates.length > 0 && (
        <div className="cev-imp">
          <h3>
            <FaRegCalendarAlt aria-hidden="true" /> Important dates
          </h3>
          {importantDates.map((d) => (
            <div key={d.id || d.label} className="cev-imp__row">
              <span className="cev-imp__date">{d.date_label}</span>
              <p>{d.label}</p>
              {d.yearly && <span className="cev-imp__yr">yearly</span>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
